<template>
    <div :key="image.id" class="col-span-1 bg-white rounded-lg shadow divide-y divide-gray-200 group relative">
        <div
            v-if="!disabled"
            @click="duplicateImage()"
            class="z-10 cursor-pointer group-hover:opacity-100 opacity-0 absolute -top-2 right-6 transition-opacity h-6 w-6 bg-blue-500 rounded-full flex flex-col justify-center items-center">
            <DocumentDuplicateIcon class="h-4 w-4 text-white"/>
        </div>
        <div
            v-if="!disabled"
            @click="image.reference ? openModal() : deleteImage()"
            class="z-10 cursor-pointer group-hover:opacity-100 opacity-0 absolute -top-2 -right-2 transition-opacity h-6 w-6 bg-red-500 rounded-full flex flex-col justify-center items-center">
            <TrashIcon class="h-4 w-4 text-white"/>
        </div>
        <div class="w-full flex items-center justify-between px-6 py-3 space-x-6">
            <div class="flex flex-col">
                <div class="flex items-center mt-2 space-x-3">
                    <h3 class="text-gray-400 text-xs font-light truncate">{{ image.reference || 'RTF--I----------' }}</h3>
                    <span :class="[image.status !== 'vacant' ? 'bg-green-100 text-green-800' : 'border border-blue-100 text-blue-800']" class="flex-shrink-0 inline-block px-2 py-0.5 text-xs font-medium rounded-full">{{ image.status || 'draft' }}</span>
                </div>
                <tailwind-select class="w-36" :flat="true" :no-label="true" value-field="id" @change="$event => categoryUpdated($event)" :options="categories" :selected-item="image.category" :disabled="disabled"/>
                <textarea
                    :disabled="disabled"
                    v-model="image.custom_size"
                    placeholder="Size Details"
                    v-if="image.category.id === 6 || image.custom_size"
                    class="mt-2 max-w-lg shadow-sm block w-full focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border border-gray-300 rounded-md p-4"></textarea>
                <div class="relative flex items-start mt-2">
                    <div class="flex items-center h-5">
                        <input :id="'is_pdf' + nodeKey" name="is_pdf" v-model="image.is_pdf" v-on:change="this.updated()" type="checkbox" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" :disabled="disabled"/>
                    </div>
                    <div class="ml-3 text-sm">
                        <label :for="'is_pdf' + nodeKey" class="font-medium text-gray-700">PDF Image</label>
                    </div>
                </div>
            </div>
            <div class="flex flex-col items-center justify-center space-y-3">
                <drop-it
                    :disabled="disabled"
                    :no-click="true"
                    :on-drop="uploadImage"
                    :max-files="1"
                    :multiple="0"
                    :accept="['image/png', 'image/jpg', 'image/jpeg', 'application/pdf']">
                    <template v-slot:default="slotProps">
                        <div v-if="!image.uploaded_image_file_name && !newImageFile" class="cursor-pointer inline-block h-20 w-16 rounded-md bg-gray-100 flex items-center justify-center" @click="slotProps.open">
                            <DocumentIcon v-if="image.is_pdf" class="text-blue-300 h-8 w-8"/>
                            <PhotographIcon v-else class="text-blue-300 h-6 w-6"/>
                        </div>
                        <div v-else-if="image.is_pdf || newImageFile" class="cursor-pointer relative group">
                            <div v-if="image.is_pdf" class="h-20 w-16 rounded-md bg-gray-100 relative" @click="!newImageFile && slotProps.open()">
                                <DocumentIcon class="text-green-300 h-8 w-8 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"/>
                                <CheckIcon v-if="!newImageFile && image.uploaded_image_file_name" class="text-green-600 h-4 w-4 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"/>
                            </div>
                            <img v-else class="object-cover inline-block h-20 w-16 rounded-md bg-gray-300" :src="newImageFile" alt="image-to-be" />
                            <div v-if="uploading"
                                 class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                <svg class="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                    <circle class="opacity-50" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="white" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                        </div>
                        <div v-else class="cursor-pointer relative group" @click="slotProps.open" >
                            <img :key="image.id" class="object-cover inline-block h-20 w-16 rounded-md bg-gray-300" :src="image.imageUrl" :alt="image.reference" />
                        </div>
                    </template>
                    <template #whenDragging>
                        <div class="cursor-pointer inline-block h-20 w-16 rounded-md bg-gray-100 text-xs flex flex-col justify-center items-center">
                            Drop Here
                        </div>
                    </template>
                </drop-it>
                <span
                    v-if="!isEditingFileName"
                    @click="!disabled && toggleFileNameEntry()"
                    class="cursor-pointer inline-block w-16 px-2 py-0.5 text-center text-green-800 text-xs font-medium bg-green-100 rounded-full">{{ image.file_name ?? "image" }}</span>
                <input
                    v-else
                    ref="fileName"
                    @keyup.esc="cancelFileNameEdit()"
                    @blur="toggleFileNameEntry()"
                    class="inline-block w-16 px-2 py-0.5 text-center text-green-800 text-xs font-medium bg-green-100 rounded-full" v-model="image.file_name">
            </div>
        </div>
        <Disclosure as="div" class="mt-2" v-slot="{ open }">
            <DisclosureButton
                class="flex w-full justify-between rounded-lg px-4 py-2 text-left text-sm font-medium text-purple-900 focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75"
            >
                <span>Notes ({{image.notes?.length ?? 0}} chars)</span>
                <ChevronUpIcon
                    :class="!open ? 'rotate-180 transform' : ''"
                    class="h-5 w-5 text-purple-500"
                />
            </DisclosureButton>
            <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-out"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
            >
                <DisclosurePanel class="px-4 pb-2 text-sm text-gray-500">
                    <div v-if="image.notes && !isEditingNote" @click="toggleNoteEdit()">{{ image.notes }}</div>
                    <div v-else-if="!disabled && !isEditingNote" class="text-indigo-600 hover:text-indigo-900 text-center cursor-pointer" @click="toggleNoteEdit()">
                        Add Note
                    </div>
                    <span v-else>No notes attached</span>
                    <div v-if="isEditingNote" class="relative">
                        <textarea
                            ref="note"
                            v-model="image.notes"
                            placeholder="Image Note"
                            @keyup.esc="cancelNoteEdit()"
                            class="max-w-lg shadow-sm block w-full focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border border-gray-300 rounded-md p-4"></textarea>
                        <CheckIcon class="h-4 w-4 text-green-800 absolute top-2 right-2 cursor-pointer" @click="toggleNoteEdit()"/>
                    </div>
                </DisclosurePanel>
            </transition>
        </Disclosure>
        <Disclosure as="div" class="mt-2" v-slot="{ open }">
            <DisclosureButton
                class="flex w-full justify-between rounded-lg px-4 py-2 text-left text-sm font-medium text-purple-900 focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75"
            >
                <span>Mails to CC ({{image.cc_email?.length ?? 0}})</span>
                <ChevronUpIcon
                    :class="!open ? 'rotate-180 transform' : ''"
                    class="h-5 w-5 text-purple-500"
                />
            </DisclosureButton>
            <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-out"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
            >
                <DisclosurePanel class="px-4 pb-2 text-sm text-gray-500">
                    <pre v-if="image.cc_email && !isEditingCCList" @click="toggleCCListEdit()">{{ ccEmail }}</pre>
                    <div v-else-if="!disabled && !isEditingCCList" class="my-4 text-indigo-600 hover:text-indigo-900 text-center cursor-pointer" @click="toggleCCListEdit()">
                        Add CC Addresses
                    </div>
                    <span v-else>No mails attached</span>
                    <div v-if="isEditingCCList" class="relative">
                        <textarea
                            ref="ccList"
                            v-model="ccEmail"
                            placeholder="CC Email addresses"
                            @keyup.esc="cancelCCListEdit()"
                            class="max-w-lg shadow-sm block w-full focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border border-gray-300 rounded-md p-4"></textarea>
                        <CheckIcon class="h-4 w-4 text-green-800 absolute top-2 right-2 cursor-pointer" @click="toggleCCListEdit()"/>
                    </div>
                </DisclosurePanel>
            </transition>
        </Disclosure>
    </div>

    <TransitionRoot appear :show="isDialogOpen" as="template">
        <Dialog as="div" @close="closeModal" class="relative z-20" :initialFocus="deleteButtonRef">
            <TransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black bg-opacity-25" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-y-auto">
                <div
                    class="flex min-h-full items-center justify-center p-4 text-center"
                >
                    <TransitionChild
                        as="template"
                        enter="duration-300 ease-out"
                        enter-from="opacity-0 scale-95"
                        enter-to="opacity-100 scale-100"
                        leave="duration-200 ease-in"
                        leave-from="opacity-100 scale-100"
                        leave-to="opacity-0 scale-95"
                    >
                        <div
                            class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
                        >
                            <DialogTitle
                                as="h3"
                                class="text-lg font-medium leading-6 text-red-900"
                            >
                                Delete photo
                            </DialogTitle>
                            <div class="mt-2">
                                <p class="text-sm text-gray-500">
                                    This is irreversible. Sure you want to delete this photo?
                                </p>
                            </div>

                            <div class="mt-4 flex flex-row">
                                <span class="flex-grow"></span>
                                <button
                                    type="button"
                                    class="inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium"
                                    @click="closeModal"
                                >
                                    No
                                </button>
                                <button
                                    type="button"
                                    ref="deleteButtonRef"
                                    class="inline-flex justify-center rounded-md border border-transparent bg-red-100 px-4 py-2 text-sm font-medium text-red-900 hover:bg-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                                    @click="deleteImage()"
                                >
                                    Yes
                                </button>
                            </div>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import DropIt from "@/Pages/Orders/Components/DropIt";
import {
    CheckIcon,
    TrashIcon,
    RewindIcon,
    DocumentIcon,
    ChevronUpIcon,
    PhotographIcon,
    DocumentDuplicateIcon,
} from "@heroicons/vue/solid";
import {
    Dialog,
    Disclosure,
    DialogTitle,
    TransitionRoot,
    TransitionChild,
    DisclosurePanel,
    DisclosureButton,
} from '@headlessui/vue'
import {ref} from "vue";
import TailwindSelect from "@/Shared/Components/TailwindSelect";
import axios from "axios";

export default {
    props: {
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        image: Object,
        categories: Array,
    },
    setup() {
        const isDialogOpen = ref(false);
        const deleteButtonRef = ref(null)
        return {
            isDialogOpen,
            deleteButtonRef
        }
    },
    computed: {
        nodeKey() {
            return this.$.vnode.component.uid;
        },
        ccEmail: {
            get() {
                return this.image.cc_email?.join("\n")
            },
            set(val) {
                if (!val || val === '') {
                    this.image.cc_email = []
                } else {
                    this.image.cc_email = val.split(/\s+/)
                }
            }
        }
    },
    data() {
        return  {
            newImage: null,
            uploading: false,
            newImageFile: null,
            isEditingNote: false,
            isEditingCCList: false,
            isEditingFileName: false,
        }
    },
    components: {
        Dialog,
        DropIt,
        Disclosure,
        DialogTitle,
        TailwindSelect,
        TransitionRoot,
        TransitionChild,
        DisclosurePanel,
        DisclosureButton,

        TrashIcon,
        CheckIcon,
        RewindIcon,
        DocumentIcon,
        ChevronUpIcon,
        PhotographIcon,
        DocumentDuplicateIcon,
    },
    emits: ['delete', 'duplicate', 'updated', 'uploaded'],
    methods: {
        categoryUpdated(category) {
            if (category.id !== 6) {
                this.image.custom_size = '';
            }
            this.image.category = category;
            this.image.category_id = category.id;
            this.updated()
        },
        updated() {
            this.$emit('updated', this.image);
        },
        duplicateImage() {
            this.$emit('duplicate', this.image);
        },
        deleteImage() {
            this.$emit('delete', this.image);
            this.closeModal();
        },
        closeModal() {
            this.isDialogOpen = false
        },
        openModal() {
            this.isDialogOpen = true
        },
        cancelCCListEdit() {
            this.isEditingCCList = !this.isEditingCCList;
            this.image.cc_email = this.ccListCache;
        },
        toggleCCListEdit() {
            this.ccListCache = this.image.cc_email
            this.isEditingCCList = !this.isEditingCCList;
            if (!this.isEditingCCList) {
                this.updated()
            } else {
                this.$nextTick(() => this.$refs.ccList.focus())
            }
        },
        cancelNoteEdit() {
            this.isEditingNote = !this.isEditingNote;
            this.image.notes = this.noteCache;
        },
        toggleNoteEdit() {
            this.noteCache = this.image.notes
            this.isEditingNote = !this.isEditingNote;
            if (!this.isEditingNote) {
                this.updated()
            } else {
                this.$nextTick(() => this.$refs.note.focus())
            }
        },
        cancelFileNameEdit() {
            this.isEditingFileName = !this.isEditingFileName;
            this.image.file_name = this.fileNameCache;
        },
        toggleFileNameEntry() {
            this.fileNameCache = this.image.file_name
            this.isEditingFileName = !this.isEditingFileName;
            if (!this.isEditingFileName) {
                this.updated()
            } else {
                this.$nextTick(() => this.$refs.fileName.focus())
            }
        },
        uploadImage(accept, rejectReasons) {
            if (!rejectReasons.length && accept.length === 1) {
                this.newImage = accept[0];
                this.newImageFile = URL.createObjectURL(accept[0]);
                this.uploadToServer();
            }
        },
        async uploadToServer() {
            if (this.uploading) {
                return;
            }
            this.uploading = true;
            this.$emit('uploaded', {image: this.image, file: this.newImage, done: () => {
                this.uploading = false;
            }});
        }
    }
}
</script>
